// GA4 analytics module — strictly gated on `cookie_consent_analytics`.
// Nothing loads, nothing fires, nothing pings until the user has given
// explicit consent. Loaded eagerly from main.jsx but deferred internally.

import { ANALYTICS_CONSENT_KEY } from '@/i18n';

const GA_ID = import.meta.env.VITE_GA_ID;
const CONSENT_EVENT = 'analytics-consent-change';

let initialized = false;

function safeRead(key) {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

export function hasAnalyticsConsent() {
  return safeRead(ANALYTICS_CONSENT_KEY) === 'true';
}

// Caller (CookieBanner) flips this when the user accepts/declines.
// Dispatching a window event lets any listener (main.jsx) reactively
// initialize GA without a page reload.
export function setAnalyticsConsent(granted) {
  safeWrite(ANALYTICS_CONSENT_KEY, granted ? 'true' : 'false');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { granted } }));
  }
  if (granted) initAnalytics();
  else teardownAnalytics();
}

export function onConsentChange(handler) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e) => handler(Boolean(e.detail?.granted));
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
}

export function initAnalytics() {
  if (initialized) return;
  if (!GA_ID) {
    if (import.meta.env.DEV) {
      console.info('[analytics] VITE_GA_ID missing — GA4 not loaded.');
    }
    return;
  }
  if (!hasAnalyticsConsent()) return;
  if (typeof window === 'undefined') return;

  // Inject gtag.js asynchronously. Tag connects to googletagmanager.com.
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  gtag('config', GA_ID, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=Lax;Secure',
    cookie_domain: 'auto',
    send_page_view: true,
  });

  initialized = true;
}

// Public event tracker — safe to call before init (no-ops if not ready).
// Use snake_case event names per GA4 conventions.
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined') return;
  if (!initialized || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

// SPA page view — call from a route-change effect.
export function trackPageView(path, title) {
  if (!initialized || typeof window.gtag !== 'function') return;
  if (!GA_ID) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
}

// Retrait du consentement — RGPD Art. 7(3) : retirer doit être aussi simple que
// consentir ET faire CESSER le traitement. Sans ça, `initialized` restait à true
// après un refus en cours de session : gtag continuait d'émettre (page_view, web
// vitals, search_term…) jusqu'au prochain rechargement complet — invisible en SPA,
// où une session dure sans reload. `ga-disable-<ID>` est le kill-switch officiel
// de gtag.js ; remettre `initialized` à false rend trackEvent no-op à nouveau.
export function teardownAnalytics() {
  if (typeof window === 'undefined') return;
  if (GA_ID) window[`ga-disable-${GA_ID}`] = true;
  try {
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  } catch {
    /* gtag absent — rien à révoquer */
  }
  initialized = false;
}
